import { LOCAL_STORAGE_KEYS } from 'components/Viewer3d/consts';
import { User } from 'models/User';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  EXHIBITION_STORAGE_KEY,
  ExhibitionSessionResponse,
  ExhibitionSessionState,
  isExhibitionSessionValid,
} from 'models/ExhibitionSession';
import { useSelectedDesignStore } from './SelectedDesignContext';

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthReady: boolean;
  user: User | null;
  exhibitionSession: ExhibitionSessionState | null;
  isExhibition: boolean;
  login: (token?: string, userBody?: any) => Promise<void>;
  startExhibitionSession: (response: ExhibitionSessionResponse, microphoneGranted: boolean) => Promise<void>;
  updateExhibitionDesign: (designId: number) => void;
  markExhibitionTourCompleted: () => void;
  dismissExhibitionTour: () => void;
  endExhibitionSession: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [exhibitionSession, setExhibitionSession] = useState<ExhibitionSessionState | null>(null);

  // Auto-login during development
  useEffect(() => {
    //restore from user local storage
    // const isDev = process.env.REACT_APP_AUTO_LOGIN === 'true';
    const token = localStorage.getItem('accessToken');

    // if (isDev && !token) {
    //   // Auto-login user for development
    //   const devUser = { email: 'dev@example.com', name: 'Dev User' };
    //   setIsAuthenticated(true);
    //   setUser(devUser);
    //   localStorage.setItem('accessToken', 'dev-token');
    // } else 
    const rawExhibition = localStorage.getItem(EXHIBITION_STORAGE_KEY);
    let restoredExhibition: ExhibitionSessionState | null = null;
    if (rawExhibition) {
      try {
        restoredExhibition = JSON.parse(rawExhibition) as ExhibitionSessionState;
      } catch {
        cleanLocalStorage();
        setIsAuthReady(true);
        return;
      }
    }

    if (rawExhibition && !isExhibitionSessionValid(restoredExhibition)) {
      const machinePrinters = readMachinePrinters();
      cleanLocalStorage();
      restoreMachinePrinters(machinePrinters);
      setIsAuthReady(true);
      return;
    }

    if (token) {
      // Restore session
      const savedUser = new User(JSON.parse(localStorage.getItem('user') || '{}'));
      setIsAuthenticated(true);
      setUser(savedUser);
      if (isExhibitionSessionValid(restoredExhibition)) {
        // Deliberately no fallback for a missing demoDesignId. Filling it from
        // currentDesignId looked like a harmless migration, but currentDesignId
        // follows the visitor: once they had created a design, a reload
        // adopted that new design as the demonstration one and the tour
        // appeared on it. A session stored before this field existed simply has
        // no demo design, so it gets no tour until the visitor logs in again.
        // Skipping the tour lasts for that visit, not for the whole session.
        //
        // It was stored, so it survived every reload: from the first time anyone
        // turned the microphone off — which counts as skipping — the stand never
        // presented the demonstration again. The welcome still played, because
        // that is the session's opening message, and then nothing followed, which
        // is exactly what a broken tour looks like. Reopening the demonstration
        // design starts it again; a visitor who skips and carries on working is
        // not interrupted, because nothing reloads under them.
        setExhibitionSession({ ...restoredExhibition!, tourDismissed: false });
        useSelectedDesignStore.getState().setDesignId(restoredExhibition!.currentDesignId);
      }
    }
    setIsAuthReady(true);
  }, []);

  const login = async (token?: string, userBody?: any) => {
    cleanLocalStorage();
    try {
      if (token) {
        await new Promise(resolve => setTimeout(resolve, 1000));

        const userData = new User(userBody);
        setIsAuthenticated(true);
        setUser(userData);

        // Use the provided token or fall back to a default token
        const accessToken = token || 'real-token';
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem(EXHIBITION_STORAGE_KEY);
        setExhibitionSession(null);

        return Promise.resolve();
      } else {
        return Promise.reject(new Error('Invalid credentials'));
      }
    } catch (error) {
      console.error('Login error:', error);
      return Promise.reject(error);
    }
  };
  const cleanLocalStorage = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.DEFAULT_PRINTERS);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.PRINT_HELPERS);
    localStorage.removeItem(EXHIBITION_STORAGE_KEY);
  }

  /**
   * The printer configuration belongs to the machine, not to whoever is signed in.
   *
   * It describes the hardware standing next to this computer, so clearing it on
   * the way out left the next person unable to print until they had set the
   * printer up again — and on an exhibition stand that next person is a visitor
   * who cannot. A later login replaces it with that user's own printers anyway,
   * so keeping it across a sign-out leaks nothing into the next session.
   */
  const readMachinePrinters = () => ({
    printers: localStorage.getItem(LOCAL_STORAGE_KEYS.DEFAULT_PRINTERS),
    printHelpers: localStorage.getItem(LOCAL_STORAGE_KEYS.PRINT_HELPERS),
  });

  const restoreMachinePrinters = (saved: ReturnType<typeof readMachinePrinters>) => {
    if (saved.printers !== null) localStorage.setItem(LOCAL_STORAGE_KEYS.DEFAULT_PRINTERS, saved.printers);
    if (saved.printHelpers !== null) localStorage.setItem(LOCAL_STORAGE_KEYS.PRINT_HELPERS, saved.printHelpers);
  };

  const storeExhibitionSession = (session: ExhibitionSessionState) => {
    setExhibitionSession(session);
    localStorage.setItem(EXHIBITION_STORAGE_KEY, JSON.stringify(session));
  };

  const startExhibitionSession = async (
    response: ExhibitionSessionResponse,
    microphoneGranted: boolean
  ) => {
    // login() clears the previous user's storage, which on a stand meant every
    // visitor arrived with the printer configuration wiped — and the
    // demonstration then reported that no printer was set up.
    const machinePrinters = readMachinePrinters();

    await login(response.token, response.user);

    restoreMachinePrinters(machinePrinters);

    const session: ExhibitionSessionState = {
      sessionId: response.sessionId,
      currentDesignId: response.designId,
      // From the server, not from designId: designId is whichever design the
      // visitor was last on, so on a resumed session it is the newest one.
      demoDesignId: response.demoDesignId,
      showcaseDesignId: response.showcaseDesignId,
      expiresAt: response.expiresAt,
      resumed: response.resumed,
      tourCompleted: false,
      tourDismissed: false,
      microphoneGranted,
    };
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
    useSelectedDesignStore.getState().setDesignId(response.designId);
    storeExhibitionSession(session);
  };

  const updateExhibitionDesign = (designId: number) => {
    if (!exhibitionSession) return;
    storeExhibitionSession({ ...exhibitionSession, currentDesignId: designId });
  };

  const markExhibitionTourCompleted = () => {
    if (!exhibitionSession || exhibitionSession.tourCompleted) return;
    storeExhibitionSession({ ...exhibitionSession, tourCompleted: true });
  };

  const dismissExhibitionTour = () => {
    if (!exhibitionSession) return;
    storeExhibitionSession({ ...exhibitionSession, tourDismissed: true });
  };

  const endExhibitionSession = () => {
    const machinePrinters = readMachinePrinters();
    cleanLocalStorage();
    restoreMachinePrinters(machinePrinters);
    useSelectedDesignStore.setState({ designId: -1, createdBy: null });
    setExhibitionSession(null);
    setIsAuthenticated(false);
    setUser(null);
  };
  const logout = () => {
    const redirectToExhibition = exhibitionSession !== null ||
      localStorage.getItem(EXHIBITION_STORAGE_KEY) !== null;
    const machinePrinters = readMachinePrinters();
    cleanLocalStorage();
    restoreMachinePrinters(machinePrinters);
    setIsAuthenticated(false);
    setUser(null);
    setExhibitionSession(null);
    window.location.href = redirectToExhibition ? '/exhibition' : '/';
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isAuthReady,
      user,
      exhibitionSession,
      isExhibition: exhibitionSession !== null,
      login,
      startExhibitionSession,
      updateExhibitionDesign,
      markExhibitionTourCompleted,
      dismissExhibitionTour,
      endExhibitionSession,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
